import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { Heading } from '@ps/ds/components/primitives/Heading/Heading'
import { Separator } from '@ps/ds/components/primitives/Separator/Separator'
import { getIdFromUrl } from '@ps/tools/helpers/getIdFromUrl'
import { useSeoMeta } from '@unhead/react'
import { useGetRequestTracking } from 'src/api/domains/service-tracking/hooks/useGetRequestTracking'
import { RequestAlert } from 'src/components/request-alert/RequestAlert'
import { RequestDetailsCard } from 'src/components/request-details-card/RequestDetailsCard'
import { NS } from 'src/constants/i18n'
import { RequestTimeline } from 'src/features/request-timeline/RequestTimeline'
import { Treatment } from 'src/features/treatment/Treatment'

import { ContactSection } from './ContactSection'
import { TermsOfService } from './TermsOfService'

export const ServiceTrackingPage = () => {
  const navigate = useNavigate()

  const params = useParams()
  const trackingCode = getIdFromUrl(params, 'trackingId')

  const { t } = useTranslation(NS.TRACKING)
  useSeoMeta({ title: t('meta.title'), robots: 'noindex, nofollow' })

  const { data, error } = useGetRequestTracking({ code: trackingCode })

  useEffect(() => {
    if (error) navigate('/404')
  }, [data, error, navigate])

  if (!data) return null

  const { groupedRequestStatus, activeWorkOrder } = data.parsedFields

  return (
    <div className="flex flex-col gap-8">
      <Heading as="h1" size="xl" weight="bold" className="sm:text-4xl">
        {t('title')}
      </Heading>
      <RequestDetailsCard
        requestNumber={data.requestNumber}
        submittedAt={data.submittedAt}
        groupedRequestStatus={data.parsedFields.groupedRequestStatus}
      />
      <div className="md:grid md:gap-[76px] md:grid-cols-[1fr_minmax(auto,380px)]">
        <div className="flex flex-col gap-8">
          <RequestAlert
            trackingPests={data.requestPests}
            requestStatus={data.requestStatus}
            denyReason={data.denyReason}
          />
          <RequestTimeline
            groupedRequestStatus={groupedRequestStatus}
            requestNumber={data.requestNumber}
            workOrderId={activeWorkOrder?.id}
            providerName={activeWorkOrder?.provider.name}
            scheduleType={activeWorkOrder?.scheduling.scheduleType}
            firstServiceDate={activeWorkOrder?.scheduling.firstServiceDate}
            lastServiceDate={activeWorkOrder?.scheduling.lastServiceDate}
          />
          <Treatment
            groupedRequestStatus={groupedRequestStatus}
            requestPests={data.requestPests}
            requestStatus={data.requestStatus}
            workOrderPestsByName={data.parsedFields.workOrderPestsByName}
          />
          <Separator />

          <div className="flex flex-col gap-8 md:hidden ">
            <ContactSection
              groupedRequestStatus={data.parsedFields.groupedRequestStatus}
              provider={activeWorkOrder?.provider}
            />
            <Separator />
          </div>

          <TermsOfService />
        </div>

        <div className="hidden md:flex md:flex-col">
          <ContactSection
            groupedRequestStatus={data.parsedFields.groupedRequestStatus}
            provider={activeWorkOrder?.provider}
          />
        </div>
      </div>
    </div>
  )
}
